import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  Balances,
  EditedRecord,
  PendingTimeOffRequest,
  RequestTypes,
  TimeOffRecord
} from '../../../models/TimeOffModels';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PTOConfig } from '../../../models/paid-time-off';
import { BehaviorSubject, Observable } from 'rxjs';
import { CompaniesService } from '../../../services/companies.service';
import {
  ClientTimeOffRequestService,
  TimeOffRequestService
} from '../../../services/time-off-request.service';
import { AdminService } from '../../../services/admin.service';
import {
  formatDatePickerValue,
  parseDatePickerValue,
  parseTimeFromDate,
  parseTimeString
} from '../../../utils/datepicker-value-parser';
import {
  AreDatesEqual,
  DoesRangeContainWeekends,
  GetDaysBetweenTwoDates,
  GetHoursBetweenTwoTimes
} from '../../../utils/dateUtils';
import { map } from 'rxjs/operators';
import {
  dateRangeComponentValidator,
  timeRangeNotEqualValidator,
  timeRangeValidator
} from '../../../utils/dateRangeValidator';
import { addDays, differenceInHours } from 'date-fns';
import { ResetForm } from '@ngxs/form-plugin';
import { Select, Selector, Store } from '@ngxs/store';
import {
  TimeOffRequestsState,
  TimeOffSetInitialApprovedRequestBalance
} from '../../../state/TimeOffRequestsState/TimeOffRequests.state';
import { ModalService } from '../../../services/modal.service';

@Component({
  selector: 'lib-client-edit-approved',
  templateUrl: './client-edit-approved.component.html',
  styleUrls: ['./client-edit-approved.component.css'],
  standalone: false
})
export class ClientEditApprovedComponent implements OnInit, OnDestroy {
  @Input()
  public clientCode: string;

  @Input()
  public existingModel: TimeOffRecord | null = null;

  @Input()
  public openRequests: PendingTimeOffRequest[] = [];
  @Input()
  public editedRequests: EditedRecord[] = [];
  public form: FormGroup;

  @Output()
  public modalClosed: EventEmitter<any> = new EventEmitter<any>();

  public timeOffPlans: PTOConfig[] = [];

  public balances: Balances[] = [];

  @Select(TimeOffRequestsState.isDateInPast)
  public showDateMessage: Observable<boolean>;
  @Select(TimeOffRequestsState.showPeriodStartDateWarning)
  public showPeriodStartDateWarning: Observable<boolean>;
  public currentRequestHoursObs: BehaviorSubject<number> | null = null;
  public initialRequestHours: number | null;
  public currentPlanObs: BehaviorSubject<number>;
  public showHolidayMessage = false;

  public balanceIsNegative: Observable<boolean>;
  public negativeBalanceAllow = false;

  public showCommentMessage = false;
  public showCommentMessageForConditionalApproval = false;

  public editMode = false;

  public originalFormVal = null;

  constructor(
    private fb: FormBuilder,
    private companiesService: CompaniesService,
    private timeOffService: ClientTimeOffRequestService,
    private employeeTimeOffService: TimeOffRequestService,
    private adminService: AdminService,
    private store: Store,
    private modalService: ModalService
  ) {}

  get partialDayHours() {
    if (this.form.get('requestType').value != RequestTypes.PartialDay) {
      return 0;
    }
    if (this.form.get('startTime').value == null || this.form.get('endTime').value == null) {
      return 0;
    }
    return this._getHoursBetweenTwoTimes(
      this.form.get('startTime').value,
      this.form.get('endTime').value
    );
  }

  get dateRangeStartExtraClasses() {
    if (this.existingModel.Removed) {
      return '';
    }
    const currentVal = this.form.get('dateRange').value;
    if (currentVal == null && this.originalFormVal.dateRange == null) {
      return '';
    }
    if (currentVal == null && this.originalFormVal.dateRange != null) {
      return 'text-cp-green';
    }
    if (currentVal != null && this.originalFormVal.dateRange == null) {
      return 'text-cp-green';
    }
    if (!AreDatesEqual(currentVal.start, this.originalFormVal.dateRange.start)) {
      return 'text-cp-green';
    }
    return '';
  }

  get dateRangeEndExtraClasses() {
    if (this.existingModel.Removed) {
      return '';
    }
    const currentVal = this.form.get('dateRange').value;
    if (currentVal == null && this.originalFormVal.dateRange == null) {
      return '';
    }
    if (currentVal == null && this.originalFormVal.dateRange != null) {
      return 'text-cp-green';
    }
    if (currentVal != null && this.originalFormVal.dateRange == null) {
      return 'text-cp-green';
    }
    if (!AreDatesEqual(currentVal.end, this.originalFormVal.dateRange.end)) {
      return 'text-cp-green';
    }
    return '';
  }

  get startTimeExtraClasses() {
    if (this.existingModel.Removed) {
      return '';
    }
    const currentVal = this.form.get('startTime').value;
    if (currentVal == null && this.originalFormVal.startTime == null) {
      return '';
    }
    if (currentVal == null && this.originalFormVal.startTime != null) {
      return 'text-cp-green';
    }
    if (currentVal != null && this.originalFormVal.startTime == null) {
      return 'text-cp-green';
    }
    if (currentVal != this.originalFormVal.startTime) {
      return 'text-cp-green';
    }
    return '';
  }

  get endTimeExtraClasses() {
    if (this.existingModel.Removed) {
      return '';
    }
    const currentVal = this.form.get('endTime').value;
    if (currentVal == null && this.originalFormVal.endTime == null) {
      return '';
    }
    if (currentVal == null && this.originalFormVal.endTime != null) {
      return 'text-cp-green';
    }
    if (currentVal != null && this.originalFormVal.endTime == null) {
      return 'text-cp-green';
    }
    if (currentVal != this.originalFormVal.endTime) {
      return 'text-cp-green';
    }
    return '';
  }

  ngOnInit() {
    this.currentPlanObs = new BehaviorSubject<number>(this.existingModel.PlanId);
    this.companiesService.getPTOConfigs(this.clientCode).subscribe(ptos => {
      this.timeOffPlans = ptos;
      this.employeeTimeOffService
        .getBalances(this.clientCode, this.existingModel.PersonId)
        .subscribe(balances => {
          this.balances = balances;
        });
      this._buildForm();
      this.balanceIsNegative = this.store
        .select(TimeOffRequestsState.getRemainingBalance(this.existingModel.PersonId))
        .pipe(
          map(bal => {
            return bal < 0;
          })
        );
    });
  }
  ngOnDestroy() {
    this.store.dispatch(new TimeOffSetInitialApprovedRequestBalance(null));
  }

  public approveRequest() {
    const formVal = this.form.value;
    const copy: TimeOffRecord = structuredClone(this.existingModel);
    copy.RequestType = formVal.requestType;
    copy.Comments = formVal.clientComments;
    switch (copy.RequestType) {
      case RequestTypes.PartialDay:
        const date = parseDatePickerValue(formVal.startDate);
        copy.StartDate = parseTimeString(formVal.startTime, date);
        copy.EndDate = parseTimeString(formVal.endTime, date);
        const hours = differenceInHours(copy.StartDate, copy.EndDate);
        copy.TimeOffDays = [
          {
            Hours: hours,
            Date: copy.StartDate,
            TimeOffRequestId: copy.Id,
            Id: null
          }
        ];
        break;
      case RequestTypes.SingleDay:
        copy.StartDate = parseDatePickerValue(formVal.startDate);
        copy.EndDate = null;
        if (copy.TimeOffDays.length != 1) {
          copy.TimeOffDays = [
            {
              Hours: formVal.hours,
              Date: parseDatePickerValue(formVal.startDate),
              TimeOffRequestId: copy.Id,
              Id: null
            }
          ];
        } else {
          copy.TimeOffDays[0].Hours = formVal.hours;
          copy.TimeOffDays[0].Date = parseDatePickerValue(formVal.startDate);
          copy.TimeOffDays[0].TimeOffRequestId = copy.Id;
        }
        break;
      case RequestTypes.MultipleDays:
        copy.StartDate = formVal.dateRange.start;
        copy.EndDate = formVal.dateRange.end;
        const daysBetweenDates = GetDaysBetweenTwoDates(copy.StartDate, copy.EndDate);
        let initialDate;
        if (typeof copy.StartDate == 'string') {
          initialDate = new Date(copy.StartDate);
        } else {
          initialDate = copy.StartDate;
        }
        copy.TimeOffDays = [];
        for (let i = 0; i < daysBetweenDates; i++) {
          let date = addDays(initialDate, i);
          copy.TimeOffDays.push({
            Hours: formVal.hours,
            Date: date,
            TimeOffRequestId: copy.Id,
            Id: null
          });
        }
        break;
    }
    this.timeOffService.editApprovedRequest(this.clientCode, copy.Id, copy).subscribe(_ => {
      this.closeModal();
    });
  }

  public closeModal() {
    this.modalClosed.emit();
  }

  public declineRequest() {
    const sub = this.modalService.confirmModalCallback.subscribe(result => {
      if (result) {
        const formVal = this.form.value;
        this.timeOffService
          .cancelApprovedRequest(
            this.clientCode,
            this.existingModel.PersonId,
            this.existingModel.Id,
            formVal.clientComments
          )
          .subscribe(_ => {
            this.closeModal();
            sub.unsubscribe();
          });
      }
    });

    this.modalService.showConfirmModal(
      'Are you sure you want to cancel this request?',
      'No',
      'Yes',
      'Confirm Cancellation'
    );
  }

  public resetForm() {
    this.form = null;
    this.editMode = false;
    setTimeout(() => {
      this._buildForm();
    }, 0);
  }

  public isWeekend() {
    const formVal = this.form.value;
    let start: Date;
    let end: Date;

    if (formVal.requestType != RequestTypes.MultipleDays) {
      if (formVal.startDate == null) return false;
      start = parseDatePickerValue(formVal.startDate);
      end = parseDatePickerValue(formVal.startDate);
    } else {
      if (formVal.dateRange?.start == null || formVal.dateRange?.end == null) {
        return false;
      }
      start = formVal.dateRange.start;
      end = formVal.dateRange.end;
    }
    return DoesRangeContainWeekends(start, end);
  }

  private _buildForm() {
    let initialHours = null;
    if (this.existingModel.RequestType != RequestTypes.PartialDay) {
      initialHours = 0;
      if (this.existingModel.TimeOffDays != null && this.existingModel.TimeOffDays.length > 0) {
        let hours = 0;
        this.existingModel.TimeOffDays.forEach(day => {
          hours += day.Hours;
        });
        initialHours = this.existingModel.TimeOffDays[0].Hours;
        this.store.dispatch(new TimeOffSetInitialApprovedRequestBalance(hours));
      }
      this.initialRequestHours = initialHours;
    }
    let startTime = null;
    let endTime = null;
    if (this.existingModel.RequestType == RequestTypes.PartialDay) {
      startTime = parseTimeFromDate(this.existingModel.StartDate);
      endTime = parseTimeFromDate(this.existingModel.EndDate);
      this.initialRequestHours = this._getHoursBetweenTwoTimes(startTime, endTime);
    }

    let rangeStart = null;
    let rangeEnd = null;
    if (this.existingModel.RequestType == RequestTypes.MultipleDays) {
      rangeStart = this.existingModel.StartDate;
      rangeEnd = this.existingModel.EndDate;
    }

    let startDate = null;
    if (this.existingModel.RequestType != RequestTypes.MultipleDays) {
      startDate = formatDatePickerValue(this.existingModel.StartDate);
    }
    const initialDateRange: any = {
      start: rangeStart,
      end: rangeEnd
    };
    this.form = this.fb.group({
      timeOffType: this.fb.control(this.existingModel.PlanId, [Validators.required]),
      requestType: this.fb.control(
        {
          value: this.existingModel.RequestType,
          disabled: this.existingModel.Removed
        },
        [Validators.required]
      ),
      startDate: this.fb.control(
        {
          value: startDate,
          disabled: this.existingModel.Removed
        },
        [Validators.required]
      ),
      hours: this.fb.control(
        {
          value: initialHours,
          disabled: this.existingModel.Removed
        },
        [Validators.required, Validators.min(0)]
      ),
      employeeComments: this.fb.control({
        value: this.existingModel.EmployeeComments,
        disabled: this.existingModel.Removed
      }),
      clientComments: this.fb.control({
        value: this.existingModel.Comments,
        disabled: this.existingModel.Removed
      }),
      startTime: this.fb.control({
        value: startTime,
        disabled: this.existingModel.Removed
      }),
      endTime: this.fb.control({
        value: endTime,
        disabled: this.existingModel.Removed
      }),
      dateRange: this.fb.control(
        {
          value: initialDateRange,
          disabled: this.existingModel.Removed
        },
        [dateRangeComponentValidator]
      )
    });
    this.store.dispatch(
      new ResetForm({ path: 'timeOffRequests.NewRequestForm', value: this.form.value })
    );
    this.currentRequestHoursObs = new BehaviorSubject(this._initCurrentRequestHours());
    this.form.get('hours').valueChanges.subscribe(val => {
      const hours = this._initCurrentRequestHours();
      this.currentRequestHoursObs.next(hours);
    });
    this.form.get('startTime').valueChanges.subscribe(val => {
      const hours = this._initCurrentRequestHours();
      this.currentRequestHoursObs.next(hours);
    });
    this.form.get('endTime').valueChanges.subscribe(val => {
      const hours = this._initCurrentRequestHours();
      this.currentRequestHoursObs.next(hours);
    });
    if (this.existingModel != null) {
      const plan = this.timeOffPlans.find(x => x.Id == this.existingModel.PlanId);

      this.negativeBalanceAllow = plan.RequestNegativeBalRule == 0;
      // check holidays
      if (this.existingModel.RequestType == RequestTypes.MultipleDays) {
        let start = this.existingModel.StartDate;
        if (typeof start == 'string') {
          start = new Date(start);
        }
        let end = this.existingModel.EndDate;
        if (typeof end == 'string') {
          end = new Date(end);
        }
        this.adminService
          .getHolidays(start, end)
          .pipe(
            map(x => {
              return x.length > 0;
            })
          )
          .subscribe(result => {
            this.showHolidayMessage = result;
          });
      } else {
        let parsed = this.existingModel.StartDate;
        if (typeof parsed == 'string') {
          parsed = new Date(parsed);
        }
        const start = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() - 1);
        const end = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() + 1);
        this.adminService
          .getHolidays(start, end)
          .pipe(
            map(x => {
              return x.length > 0;
            })
          )
          .subscribe(result => {
            this.showHolidayMessage = result;
          });
      }
    }
    this.originalFormVal = this.form.value;
    this.form.setValidators([
      timeRangeValidator('startTime', 'endTime'),
      timeRangeNotEqualValidator('startTime', 'endTime')
    ]);
    this.form.updateValueAndValidity();
    this._setFormValidation(this.form.get('requestType').value, true);
    this.form.get('requestType').valueChanges.subscribe(val => {
      this._setFormValidation(val);
    });
    this.form.valueChanges.subscribe(formVal => {
      this.editMode =
        formVal.requestType != this.originalFormVal.requestType ||
        !this._areDateRangesEqual(formVal.dateRange, this.originalFormVal.dateRange) ||
        formVal.endTime != this.originalFormVal.endTime ||
        formVal.hours != this.originalFormVal.hours ||
        !this._areDatepickersEqual(formVal.startDate, this.originalFormVal.startDate) ||
        formVal.startTime != this.originalFormVal.startTime;
    });
    this.form.get('startDate').valueChanges.subscribe(startDate => {
      if (startDate == null) {
        return;
      }
      const parsed = parseDatePickerValue(startDate);
      const start = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() - 1);
      const end = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() + 1);
      this.adminService
        .getHolidays(start, end)
        .pipe(
          map(x => {
            return x.length > 0;
          })
        )
        .subscribe(result => {
          this.showHolidayMessage = result;
        });
    });
    this.form.get('clientComments').valueChanges.subscribe(_ => {
      this.showCommentMessage = false;
    });
    this.form.get('dateRange').valueChanges.subscribe(range => {
      if (range?.start == null || range?.end == null) {
        return;
      }
      const start = range.start;
      const end = range.end;
      this.adminService
        .getHolidays(start, end)
        .pipe(
          map(x => {
            return x.length > 0;
          })
        )
        .subscribe(result => {
          this.showHolidayMessage = result;
        });
    });

    this.form.get('timeOffType').valueChanges.subscribe(val => {
      const plan = this.timeOffPlans.find(x => x.Id == val);

      this.negativeBalanceAllow = plan.RequestNegativeBalRule == 0;
    });
  }

  private _getHoursBetweenTwoTimes(startTime: Date | string, endTime: Date | string) {
    const start = typeof startTime == 'string' ? parseTimeString(startTime) : startTime;
    const end = typeof endTime == 'string' ? parseTimeString(endTime) : endTime;
    return +(Math.abs(end.getTime() - start.getTime()) / 36e5).toFixed(2);
  }

  public _areDateRangesEqual(a, b) {
    return AreDatesEqual(a?.start, b?.start) && AreDatesEqual(a?.end, b?.end);
  }

  public _areDatepickersEqual(a, b) {
    if (a == null && b == null) {
      return true;
    }
    if (a != null && b == null) {
      return false;
    }
    if (a == null && b != null) {
      return false;
    }
    return a.year == b.year && a.month == b.month && a.day == b.day;
  }

  private _setFormValidation(val: RequestTypes, initial = false) {
    if (!initial) {
      this.form.get('startDate').reset();
      this.form.get('hours').reset();
      this.form.get('startTime').reset();
      this.form.get('endTime').reset();
      this.form.get('dateRange').reset();
    }
    if (val == RequestTypes.SingleDay) {
      this.form.get('startDate').setValidators(Validators.required);
      this.form.get('startDate').updateValueAndValidity();
      this.form
        .get('hours')
        .setValidators([Validators.required, Validators.min(1), Validators.max(24)]);
      this.form.get('hours').updateValueAndValidity();
      this.form.get('startTime').clearValidators();
      this.form.get('startTime').updateValueAndValidity();
      this.form.get('endTime').clearValidators();
      this.form.get('endTime').updateValueAndValidity();
      this.form.get('dateRange').clearValidators();
      this.form.get('dateRange').updateValueAndValidity();
    } else if (val == RequestTypes.PartialDay) {
      this.form.get('startDate').setValidators(Validators.required);
      this.form.get('startDate').updateValueAndValidity();
      this.form.get('hours').clearValidators();
      this.form.get('hours').updateValueAndValidity();
      this.form.get('startTime').setValidators(Validators.required);
      this.form.get('startTime').updateValueAndValidity();
      this.form.get('endTime').setValidators(Validators.required);
      this.form.get('endTime').updateValueAndValidity();
      this.form.get('dateRange').clearValidators();
      this.form.get('dateRange').updateValueAndValidity();
    } else if (val == RequestTypes.MultipleDays) {
      this.form
        .get('hours')
        .setValidators([Validators.required, Validators.min(1), Validators.max(24)]);
      this.form.get('hours').updateValueAndValidity();
      this.form.get('dateRange').setValidators([Validators.required, dateRangeComponentValidator]);
      this.form.get('dateRange').updateValueAndValidity();
      this.form.get('startDate').clearValidators();
      this.form.get('startDate').updateValueAndValidity();
      this.form.get('startTime').clearValidators();
      this.form.get('startTime').updateValueAndValidity();
      this.form.get('endTime').clearValidators();
      this.form.get('endTime').updateValueAndValidity();
    }
    this.form.updateValueAndValidity();
  }
  private _initCurrentRequestHours() {
    if (this.existingModel.Removed) {
      return 0;
    }
    const requestType: RequestTypes = this.form.get('requestType').value;
    switch (requestType) {
      case RequestTypes.PartialDay:
        return GetHoursBetweenTwoTimes(
          this.form.get('startTime').value,
          this.form.get('endTime').value
        );
      case RequestTypes.SingleDay:
        return this.form.get('hours').value;
      case RequestTypes.MultipleDays:
        return (
          this.form.get('hours').value *
          GetDaysBetweenTwoDates(
            this.form.get('dateRange').value?.start,
            this.form.get('dateRange').value?.end
          )
        );
    }
  }

  protected readonly RequestTypes = RequestTypes;
}
